import { hasUnbilledEnvironment, type BillingUsage } from '@/services/billing/transforms';
import { FALLBACK_BUILDER_CREDIT_COST_PER_MESSAGE } from './builder-credits';

/**
 * The usage figures the Your Plan displays read, sourced from the billing service's `usage` atom
 * (`AccountsUsageInfo`, snake_case).
 *
 * `totalCustomerCount` / `totalObjectCount`: the account usage only represents billable workspaces,
 * so when any environment is unbilled the totals are the sum across environments; otherwise the
 * top-level totals are used. The high-watermark totals read the top-level fields directly.
 */
export interface UsageMetrics {
  totalCustomerCount: number;
  totalObjectCount: number;
  billableMessagesSent: number;
  highWatermarkCustomersTotal: number;
  highWatermarkObjectsTotal: number;
  monthlyActiveUsers: number;
  builderCreditCostPerMessage: number;
}

/** The empty usage metrics. Used before usage loads. */
export function emptyUsageMetrics(): UsageMetrics {
  return {
    totalCustomerCount: 0,
    totalObjectCount: 0,
    billableMessagesSent: 0,
    highWatermarkCustomersTotal: 0,
    highWatermarkObjectsTotal: 0,
    monthlyActiveUsers: 0,
    builderCreditCostPerMessage: FALLBACK_BUILDER_CREDIT_COST_PER_MESSAGE,
  };
}

/**
 * Derives the {@link UsageMetrics} from the billing service's account usage. Returns the empty
 * metrics when usage hasn't loaded yet.
 */
export function deriveUsageMetrics(usage: BillingUsage | undefined): UsageMetrics {
  if (usage == null) {
    return emptyUsageMetrics();
  }

  const { environments } = usage;
  const hasUnbilled = hasUnbilledEnvironment(environments);

  const totalCustomerCount = hasUnbilled
    ? environments.reduce((sum, environment) => sum + environment.customersTotal, 0)
    : usage.customersTotal;
  const totalObjectCount = hasUnbilled
    ? environments.reduce((sum, environment) => sum + environment.objectsTotal, 0)
    : usage.objectsTotal;

  return {
    totalCustomerCount,
    totalObjectCount,
    billableMessagesSent: usage.billableMessagesSent,
    highWatermarkCustomersTotal: usage.highWatermarkCustomersTotal,
    highWatermarkObjectsTotal: usage.highWatermarkObjectsTotal,
    monthlyActiveUsers: usage.monthlyActiveUsers,
    builderCreditCostPerMessage: usage.builderCreditCostPerMessage,
  };
}
